@let isCollapsed = collapsed();
@let badges = navBadges();

<ion-content [fullscreen]="true">
  <div
    class="menu-sheet flex min-h-full flex-col bg-white"
    [class]="isCollapsed ? 'items-center p-2' : 'py-2 pr-2 pl-3'"
  >
    <div
      class="border-splash flex shrink-0 border-b"
      [class]="isCollapsed ? 'w-full justify-center py-4' : contentWidthClass"
    >
      @if (isCollapsed) {
        <span [class]="logoMarkClass">
          <img class="size-full object-contain" [src]="logoMark" alt="Medlink" />
        </span>
      } @else {
        <img class="w-full" [src]="logoFull" alt="Medlink" />
      }
    </div>

    <nav
      [class]="(isCollapsed ? 'w-full items-center ' : contentWidthClass + ' ') + 'py-4'"
      [attr.aria-label]="'shell.nav.mainAria' | mlTranslate"
    >
      <div class="flex flex-col gap-1" [class]="isCollapsed ? 'w-full items-center' : 'w-full'">
        @for (item of navItems; track item.id) {
          <ion-menu-toggle [autoHide]="false" [class]="isCollapsed ? 'flex justify-center' : 'w-full'">
            <a
              [routerLink]="item.url"
              routerDirection="root"
              [replaceUrl]="true"
              routerLinkActive="is-selected"
              [routerLinkActiveOptions]="{ exact: false }"
              #rla="routerLinkActive"
              [attr.title]="item.labelKey | mlTranslate"
              class="text-body-small flex items-center rounded-lg text-neutral-300 transition-colors"
              [class]="isCollapsed ? collapsedControlClass : 'w-full'"
              [class.gap-3]="!isCollapsed"
              [class.px-3]="!isCollapsed"
              [class.py-2.5]="!isCollapsed"
              [class.bg-primary-50]="rla.isActive"
              [class.text-foreground]="rla.isActive"
              [class.font-semibold]="rla.isActive"
            >
              <span [class]="iconTrackClass">
                <ml-icon [name]="item.icon" size="sm" />
                @if (isCollapsed && badges[item.id]; as badge) {
                  <ion-badge
                    class="menu-badge menu-badge-icon"
                    [attr.aria-label]="'shell.menu.pendingNotificationsAria' | mlTranslate: { count: badge }"
                  >
                    {{ badge }}
                  </ion-badge>
                }
              </span>
              @if (!isCollapsed) {
                <span class="flex-1">{{ item.labelKey | mlTranslate }}</span>
                @if (badges[item.id]; as badge) {
                  <ion-badge
                    class="menu-badge menu-badge-label"
                    [attr.aria-label]="'shell.menu.pendingNotificationsAria' | mlTranslate: { count: badge }"
                  >
                    {{ badge }}
                  </ion-badge>
                }
              }
            </a>
          </ion-menu-toggle>
        }
      </div>
    </nav>

    <div
      class="border-splash shrink-0 border-t"
      [class]="isCollapsed ? 'w-full' : contentWidthClass"
      [class.my-1]="isCollapsed"
      [class.my-2]="!isCollapsed"
    ></div>

    @if (isCollapsed) {
      <nav class="w-full" [attr.aria-label]="'shell.nav.moreAria' | mlTranslate">
        <div class="flex w-full flex-col items-center justify-center gap-1">
          <ion-menu-toggle [autoHide]="false" class="flex justify-center">
            <a
              [routerLink]="moreRoute"
              routerDirection="root"
              [replaceUrl]="true"
              routerLinkActive="is-selected"
              [routerLinkActiveOptions]="{ exact: false }"
              #moreRla="routerLinkActive"
              [attr.title]="'shell.nav.more' | mlTranslate"
              [class]="collapsedControlClass + ' rounded-lg text-neutral-300 transition-colors'"
              [class.bg-primary-50]="moreRla.isActive"
              [class.text-foreground]="moreRla.isActive"
              [class.font-semibold]="moreRla.isActive"
            >
              <ml-icon name="ellipsesMoreDots" size="sm" />
            </a>
          </ion-menu-toggle>
        </div>
      </nav>
    } @else {
      <nav
        class="flex flex-col gap-2 py-2"
        [class]="contentWidthClass"
        [attr.aria-label]="'shell.nav.resourcesAria' | mlTranslate"
      >
        @for (link of secondaryLinks; track link.id) {
          <a
            [href]="link.url"
            target="_blank"
            rel="noopener noreferrer"
            class="text-body-small px-3 text-neutral-300 transition-colors hover:text-neutral-500"
          >
            {{ link.labelKey | mlTranslate }}
          </a>
        }
      </nav>
    }

    <div
      class="mt-auto flex flex-col gap-4 pt-4 pb-6"
      [class]="isCollapsed ? 'w-full items-center' : contentWidthClass"
    >
      <div [class]="isCollapsed ? 'flex w-full justify-center' : 'w-full px-3'">
        <ml-select
          class="w-full"
          [class.max-w-12]="isCollapsed"
          size="sm"
          [ngModel]="selectedLanguage()"
          (ngModelChange)="onLanguageChange($event)"
          [options]="languageOptions"
          [showClearBtn]="false"
          [required]="true"
          displayKey="label"
          optionIdKey="value"
          [attr.aria-label]="'shell.menu.language' | mlTranslate"
        >
          <ng-template mlSelectValue let-lang>
            @if (isCollapsed) {
              <span class="flex items-center justify-center text-neutral-900">
                <ml-icon name="language" size="sm" />
              </span>
            } @else {
              <span class="flex items-center gap-2 text-neutral-900">
                <ml-icon name="language" size="sm" />
                {{ lang.label }} ({{ lang.code }})
              </span>
            }
          </ng-template>

          <ng-template mlSelectOption let-lang>
            <span class="text-body-small flex-auto text-neutral-900">{{ lang.label }}</span>
            <span class="text-body-small text-neutral-300">{{ lang.code }}</span>
          </ng-template>
        </ml-select>
      </div>

      <ion-menu-toggle [autoHide]="false" [class]="isCollapsed ? 'flex justify-center' : 'w-full'">
        <button
          type="button"
          [attr.title]="'shell.menu.logout' | mlTranslate"
          class="text-body-small flex items-center rounded-lg text-neutral-300 transition-colors hover:text-neutral-500"
          [class]="isCollapsed ? collapsedControlClass : 'w-full'"
          [class.gap-3]="!isCollapsed"
          [class.px-3]="!isCollapsed"
          [class.py-2.5]="!isCollapsed"
          (click)="logout()"
        >
          <span [class]="iconTrackClass">
            <ml-icon name="logout" size="sm" />
          </span>
          @if (!isCollapsed) {
            <span>{{ 'shell.menu.logout' | mlTranslate }}</span>
          }
        </button>
      </ion-menu-toggle>

      @if (!isCollapsed) {
        <div class="text-caption px-3 text-neutral-300">
          <p>
            v{{ appVersion }}
            @if (!isProduction) {
              -alpha
            }
          </p>
          @if (!isProduction) {
            <p>{{ 'shell.menu.internalTesting' | mlTranslate }}</p>
          }
        </div>
      }
    </div>
  </div>
</ion-content>
